import { getDocumentDisplayName } from '@pages/Admin/Requests/requests.utils'

export type ComplianceDocumentState = 'approved' | 'rejected' | 'not verified'

export type ComplianceRequiredDocument = {
  id?: string
  name: string
  is_uploaded?: boolean
  state?: ComplianceDocumentState
  support_comment?: string | null
  file_id?: number | string | null
}

export type DocumentsCompliancePayload = {
  is_blocked?: boolean
  show_popup?: boolean
  can_close_popup?: boolean
  deadline_at?: number
  deadline_date?: string
  days_limit?: number
  days_left?: number
  popup_title?: string
  popup_subtitle?: string
  required_documents?: ComplianceRequiredDocument[]
  popup_documents?: ComplianceRequiredDocument[]
  children_documents_compliance?: ChildDocumentsCompliance[]
}

export type ChildDocumentsCompliance = DocumentsCompliancePayload & {
  child_id: number
  child_name: string
}

export type UserDocument = {
  id?: number | string
  path?: string
  name?: string
  document_name?: string
  is_approved?: number
  status?: number
}

export type ComplianceDocumentStateTone =
  | 'approved'
  | 'rejected'
  | 'pending'
  | 'notUploaded'
  | 'selected'

export type ComplianceDocumentStateView = {
  label: string
  tone: ComplianceDocumentStateTone
}

export const parseComplianceFileId = (fileId: unknown): number | null => {
  const parsed = Number(fileId)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return parsed
}

export const getRequiredDocumentFileIds = (
  requiredDocuments: ComplianceRequiredDocument[] = [],
): Set<number> => {
  const ids = new Set<number>()

  requiredDocuments.forEach((document) => {
    const fileId = parseComplianceFileId(document?.file_id)
    if (fileId) ids.add(fileId)
  })

  return ids
}

export const findUserDocumentByFileId = (
  userDocuments: UserDocument[] = [],
  fileId: unknown,
): UserDocument | null => {
  const targetId = parseComplianceFileId(fileId)
  if (!targetId) return null

  return (
    userDocuments.find((document) => Number(document?.id) === targetId) ?? null
  )
}

export const getRequiredDocumentDisplayName = (
  requiredDocument: ComplianceRequiredDocument,
  userDocuments: UserDocument[] = [],
): string => {
  const requiredName =
    String(requiredDocument?.name ?? '').trim() || 'Документ'

  // После одобрения имя берём из documents (то, что админ задал),
  // иначе — name из required_documents.
  if (requiredDocument?.state !== 'approved') {
    return requiredName
  }

  const matchedFile = findUserDocumentByFileId(
    userDocuments,
    requiredDocument?.file_id,
  )
  if (!matchedFile) return requiredName

  const fromDocuments = String(getDocumentDisplayName(matchedFile) ?? '').trim()
  return fromDocuments || requiredName
}

export const filterNonRequiredUserDocuments = (
  userDocuments: UserDocument[] = [],
  requiredDocuments: ComplianceRequiredDocument[] = [],
): UserDocument[] => {
  const requiredFileIds = getRequiredDocumentFileIds(requiredDocuments)

  return userDocuments.filter(
    (document) => !requiredFileIds.has(Number(document?.id)),
  )
}

export const isRequiredDocumentUploaded = (
  document: ComplianceRequiredDocument,
): boolean =>
  Boolean(document?.is_uploaded) && document?.state !== 'rejected'

export const getCompliancePopupDocuments = (
  compliance?: DocumentsCompliancePayload | null,
): ComplianceRequiredDocument[] => {
  const popupDocuments = compliance?.popup_documents
  if (Array.isArray(popupDocuments) && popupDocuments.length) {
    return popupDocuments
  }

  const requiredDocuments = compliance?.required_documents
  return Array.isArray(requiredDocuments) ? requiredDocuments : []
}

export const getParentChildrenComplianceList = (
  compliance?: DocumentsCompliancePayload | null,
): ChildDocumentsCompliance[] => {
  const children = compliance?.children_documents_compliance
  if (!Array.isArray(children)) return []

  return children.filter(
    (item) => item?.child_id != null && String(item?.child_name ?? '').trim(),
  ) as ChildDocumentsCompliance[]
}

export const getParentChildrenWithPopupCompliance = (
  compliance?: DocumentsCompliancePayload | null,
): ChildDocumentsCompliance[] =>
  getParentChildrenComplianceList(compliance).filter((item) =>
    Boolean(item?.show_popup),
  )

export const findChildDocumentsCompliance = (
  compliance: DocumentsCompliancePayload | null | undefined,
  childId: unknown,
): ChildDocumentsCompliance | null => {
  const targetChildId = Number(childId)
  if (!Number.isFinite(targetChildId) || targetChildId <= 0) return null

  return (
    getParentChildrenComplianceList(compliance).find(
      (item) => Number(item.child_id) === targetChildId,
    ) ?? null
  )
}

export const getComplianceDocumentFileKey = (
  document: ComplianceRequiredDocument,
  childId?: number | null,
): string => {
  const documentKey = document?.id ?? document?.name
  if (childId != null && Number.isFinite(Number(childId))) {
    return `${childId}:${documentKey}`
  }

  return String(documentKey)
}

export const formatComplianceDeadlineDate = (
  deadlineDate: unknown,
): string => {
  const raw = String(deadlineDate ?? '').trim()
  if (!raw) return ''

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/,
  )
  if (!match) return raw

  const [, year, month, day, hours, minutes] = match
  return `${day}.${month}.${year} ${hours}:${minutes}`
}

export const getComplianceDeadlineSubtitle = (
  deadlineDate: unknown,
): string => {
  const formatted = formatComplianceDeadlineDate(deadlineDate)
  if (!formatted) return ''

  return `Загрузите необходимые документы до ${formatted}, иначе доступ к платформе будет закрыт.`
}

export const REQUIRED_DOCUMENTS_ACCESS_RESTORED_EVENT =
  'requiredDocumentsComplianceAccessRestored'

export const REQUIRED_DOCUMENTS_COMPLIANCE_PROFILE_EXPAND = [
  'roles',
  'userProfile',
  'documents',
  'documents_compliance',
  'children',
  'gamificationAchievements',
  'achievement_reward',
  'gamification_pulses',
] as const

export const getComplianceDocumentStateView = (
  document: ComplianceRequiredDocument,
  options: { hasSelectedFile?: boolean } = {},
): ComplianceDocumentStateView => {
  if (options.hasSelectedFile) {
    return { label: 'Файл выбран', tone: 'selected' }
  }

  if (document?.state === 'approved') {
    return { label: 'Принят', tone: 'approved' }
  }

  if (document?.state === 'rejected') {
    return { label: 'Отклонён', tone: 'rejected' }
  }

  if (document?.state === 'not verified' && !document?.is_uploaded) {
    return { label: 'Не загружен', tone: 'notUploaded' }
  }

  if (document?.state === 'not verified') {
    return { label: 'На проверке', tone: 'pending' }
  }

  return { label: 'Не загружен', tone: 'notUploaded' }
}
